/* eslint-disable max-lines -- this is the single orchestrator for the whole search flow
   (request build, URL round-trip, inferred-filter merge, job mode); keeping that one lifecycle
   in one file reads better than scattering it across modules to satisfy a line count. */
import { SearchPersistence } from '@/core/shared/search/search.persistence';
import { JobPersistence } from '@/core/shared/job/job.persistence';
import type { JobLocationCoordinates } from '@/core/shared/job/job.persistence';
import type { PlacePrediction } from '@/core/shared/search/types/place-prediction.type';
import type { JobWithRelations } from '@factoryfixinc/ats-interfaces';
import { JobNotFoundError } from '@/core/shared/errors/job-not-found.error';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { getJobTitle } from '@/core/sourcing/utils/job';
import { JobUtil } from '@/utils/job.util';
import { stripHtml } from '@/utils/strings/strip-html.utils';
import { type DossierFilterState, useSearchStore } from './search.store';
import { SelectedFilterType } from '@/core/sourcing/search/types/selected-filter-type.type';
import { DEFAULT_AROUND_RADIUS } from '@/core/sourcing/search/consts/default-seach-query.const';
import { detectTypedMode } from '@/core/sourcing/search/utils/detect-mode.util';
import { SearchTab } from '@/core/sourcing/search/types/search-tab.enum';
import type { SearchMode } from '@/core/sourcing/search/types/search-mode.type';
import type { SelectedFilter } from '@/core/sourcing/search/types/selected-filter.type';
import { SearchService as SourcingSearchService } from '@/core/sourcing/search/search.service';
import type { RecentSearch } from '@/core/sourcing/search/types/recent-search.type';
import { ErrorService } from '../errors/error.service';
import { useMeStore } from '../me/me.store';
import JobApplicantService from '../job-applicant/job-applicant.service';
import type { LocationQuery } from 'vue-router';
import { ViewedCandidateService } from '@/core/sourcing/viewed-candidates/viewed-candidate.service';
import {
  FILTERABLE_LANGUAGES,
  FILTERABLE_RESPONSIVENESS,
  FILTERABLE_SHIFTS,
  type FilterableLanguage,
  type FilterableResponsiveness,
  type FilterableShift,
  MAX_HOURLY_PAY_RATE,
  RECENT_OPEN_TO_WORK_DAYS,
} from '@/core/sourcing/search/consts/hard-filters.const';

// A duplicated query key (?shifts=a&shifts=b) parses as a string[] at runtime despite
// the declared Record<string, string> type (same trap the `inferred` param guards), so
// both readers accept only a plain string and treat anything else as absent.

/**
 * Parses a pay-rate URL param. Anything that isn't a positive hourly dollar amount the
 * server would accept reads as "unset" — staging it would produce a filter the server
 * silently drops, which looks applied but isn't.
 */
const readPayRate = (param: unknown): number | undefined => {
  const amount =
    typeof param === 'number' ? param : typeof param === 'string' && param ? Number(param) : NaN;

  if (!Number.isFinite(amount) || amount <= 0 || amount > MAX_HOURLY_PAY_RATE) {
    return undefined;
  }

  return amount;
};

/** Parses a comma-joined URL param, keeping only members of the allowed vocabulary. */
const readListParam = <T extends string>(
  param: string | null | undefined,
  allowed: readonly T[],
): T[] =>
  (typeof param === 'string' ? param : '')
    .split(',')
    .map((entry) => entry.trim().toLowerCase())
    .filter((entry): entry is T => allowed.includes(entry as T));

/** The insight filters the backend can infer; pay is one control group over two types. */
const DOSSIER_FILTER_TYPES = [
  SelectedFilterType.PAY_RATE_MIN,
  SelectedFilterType.PAY_RATE_MAX,
  SelectedFilterType.SHIFT,
  SelectedFilterType.WILLING_TO_RELOCATE,
  SelectedFilterType.OPEN_TO_TRAVEL,
  SelectedFilterType.LANGUAGE,
  SelectedFilterType.RESPONSIVENESS,
  SelectedFilterType.LAST_OPEN_TO_OPPORTUNITIES,
] as const;

type DossierFilterType = (typeof DOSSIER_FILTER_TYPES)[number];

const isDossierFilterType = (type: SelectedFilterType): type is DossierFilterType =>
  (DOSSIER_FILTER_TYPES as readonly SelectedFilterType[]).includes(type);

const unhandledDossierType = (type: never): never => {
  throw new Error(`Unhandled dossier filter type: ${String(type)}`);
};

const OPPOSITE_PAY_BOUND: Partial<Record<DossierFilterType, DossierFilterType>> = {
  [SelectedFilterType.PAY_RATE_MIN]: SelectedFilterType.PAY_RATE_MAX,
  [SelectedFilterType.PAY_RATE_MAX]: SelectedFilterType.PAY_RATE_MIN,
};

// Monotonic token for "search from a job" selection. selectActiveJob mutates shared store
// state across two awaits (job load, then coordinate lookup); a rapid second pick bumps this
// so the slower first pick sees its token go stale after either await and bails before
// committing — keeping activeJob and its coordinates from landing on mismatched jobs and from
// double-firing the auto-search.
let activeJobSelectionToken = 0;

export class SearchService {
  private persistence = new SearchPersistence();
  private jobPersistence = new JobPersistence();
  private store = useSearchStore();

  public getAddressPredictions(address: string): Promise<PlacePrediction[]> {
    return this.persistence.getAddressPredictions(address);
  }

  public get activeJob(): JobWithRelations<'jobTitle'> | undefined {
    return this.store.activeJob;
  }

  public set activeJob(activeJob: JobWithRelations<'jobTitle'> | undefined) {
    this.store.activeJob = activeJob;
  }

  /**
   * Fetches the full job for "search from a job" mode. Throws JobNotFoundError when the job
   * no longer exists (an expected outcome the caller can recover from) and lets any other
   * fetch failure propagate. Returns the job rather than committing it so selectActiveJob can
   * commit under its latest-pick guard.
   */
  public async loadActiveJob(jobId: number): Promise<JobWithRelations<'jobTitle'>> {
    const job = await this.jobPersistence.getByIdWithRelations(jobId, ['jobTitle']);

    if (!job) {
      throw new JobNotFoundError(jobId);
    }

    return job;
  }

  /**
   * Banner data for the active-job affordance, or null. `summary` is the one-sentence,
   * display-only description of the job search shown in the (disabled) query input;
   * the request itself is built from the job fields in syncFiltersToSourcingSearch,
   * never from this string.
   */
  public get activeJobBanner(): { title: string; location: string; summary: string } | null {
    const job = this.store.activeJob;

    if (!job) {
      return null;
    }

    return {
      title: getJobTitle(job, false).displayTitle,
      location: this.composeJobLocation(job),
      summary: this.composeJobSummary(job),
    };
  }

  public get locationInferred(): boolean {
    return this.store.locationInferred;
  }

  public set locationInferred(locationInferred: boolean) {
    this.store.locationInferred = locationInferred;
  }

  public get distanceInferred(): boolean {
    return this.store.distanceInferred;
  }

  public set distanceInferred(distanceInferred: boolean) {
    this.store.distanceInferred = distanceInferred;
  }

  public get lastActiveInferred(): boolean {
    return this.store.lastActiveInferred;
  }

  public set lastActiveInferred(lastActiveInferred: boolean) {
    this.store.lastActiveInferred = lastActiveInferred;
  }

  /**
   * Records that the recruiter removed an inferred filter, so re-running the current query
   * doesn't immediately re-infer it (the X would otherwise be a no-op for NL searches: the
   * source text still names the constraint). Suppressions are keyed to the query they were
   * created under; syncFiltersToSourcingSearch drops them once the query is edited.
   */
  public suppressInferredType(type: SelectedFilterType): void {
    if (this.store.suppressedForKeyword !== this.store.keyword) {
      this.store.suppressedInferredTypes = [];
    }

    this.store.suppressedForKeyword = this.store.keyword;

    const suppressed = this.store.suppressedInferredTypes ?? [];
    if (!suppressed.includes(type)) {
      this.store.suppressedInferredTypes = [...suppressed, type];
    }
  }

  public get activeJobCoordinates(): JobLocationCoordinates | undefined {
    return this.store.activeJobCoordinates;
  }

  public set activeJobCoordinates(coordinates: JobLocationCoordinates | undefined) {
    this.store.activeJobCoordinates = coordinates;
  }

  private composeJobLocation(job: JobWithRelations<'jobTitle'>): string {
    return [job.city, job.state].filter(Boolean).join(', ');
  }

  /**
   * One-sentence, human-readable summary of a job-mode search, composed locally from job
   * fields — e.g. "Maintenance Technician on 2nd Shift, $28 - $34 /hr, in
   * Cincinnati, OH". Fields the job lacks are simply omitted; the gates check the raw job
   * fields (not JobUtil's output) because getShift/getPayRange return sentinel strings
   * ('No Shift', 'Undisclosed Pay Rate') that must never read as part of the sentence —
   * the sentinel comparisons below are a second guard for fields that are set but still
   * unformattable (e.g. a custom shift missing its start/end times).
   */
  private composeJobSummary(job: JobWithRelations<'jobTitle'>): string {
    const title = getJobTitle(job, false).displayTitle;

    // JobShift.CUSTOM_STARTTIME_AND_ENDTIME is 0, so check against null/undefined.
    const shiftLabel = job.shift != null ? JobUtil.getShift(job) : '';
    const shift =
      shiftLabel === 'No Shift' || shiftLabel === 'No shift specified' ? '' : shiftLabel;

    const hasPayValues = !!(
      job.payRateMin ||
      job.payRateMax ||
      job.payRateSalaryMin ||
      job.payRateSalaryMax
    );
    const payLabel = hasPayValues ? JobUtil.getPayRange(job) : '';
    const pay = payLabel === 'Undisclosed Pay Rate' ? '' : payLabel;

    const location = this.composeJobLocation(job);

    return [
      title,
      shift ? ` on ${shift}` : '',
      pay ? `, ${pay}` : '',
      location ? `, in ${location}` : '',
    ].join('');
  }

  /**
   * Resolves the job's coordinates for the job-mode geo filter. Best-effort: coordinates
   * are an optimization over server-side string geocoding, so a failure degrades to the
   * location string rather than blocking job selection.
   */
  private async fetchJobCoordinates(jobId: number): Promise<JobLocationCoordinates | undefined> {
    try {
      return await this.jobPersistence.getJobLocation(jobId);
    } catch (error) {
      ErrorService.captureException(error);
      return undefined;
    }
  }

  /**
   * Picks a job for job-mode search: loads the full job and clears the typed query so the
   * active job is the unambiguous source of truth. Reports the outcome explicitly so callers
   * don't have to infer failure from `activeJob` being unset:
   *   - 'loaded'    job loaded; active job is set.
   *   - 'not-found' job no longer exists — an expected outcome the caller recovers from
   *                 (the recent-search rerun falls back to a text search); no toast/Sentry.
   *   - 'error'     unexpected failure; captured to Sentry and surfaced with a critical toast.
   */
  public async selectActiveJob(
    jobId: number,
  ): Promise<'loaded' | 'not-found' | 'error' | 'superseded'> {
    // Claim the latest selection up front; a second pick bumps the token and supersedes this
    // one. The two awaits below each yield, so re-check after both and bail before any commit
    // if a newer pick has taken over — otherwise a slower pick could overwrite the newer job
    // or its coordinates, or auto-run a search against mismatched state.
    const token = ++activeJobSelectionToken;
    try {
      const job = await this.loadActiveJob(jobId);
      if (token !== activeJobSelectionToken) {
        return 'superseded';
      }
      // Active job is the source of truth: store it, clear the typed query, and seed the
      // sidebar LOCATION from the job (flagged inferred for the sparkle; a user edit wins).
      this.store.activeJob = job;
      this.store.keyword = undefined;
      // Job mode only infers LOCATION; an NL-inferred distance/recency from a prior typed
      // search must not linger against the new job context — drop the sparkle and the value
      // it owned, or the job search silently fences on a stale constraint with no sparkle to
      // reveal it. A user-set value is left alone (it's the recruiter's explicit preference).
      if (this.store.distanceInferred) {
        this.store.distance = undefined;
      }
      if (this.store.lastActiveInferred) {
        this.store.lastActive = undefined;
      }
      this.store.distanceInferred = false;
      this.store.lastActiveInferred = false;
      this.mergeInferredDossierFilters(new Map());
      const location = this.composeJobLocation(job);
      this.store.location = location || undefined;
      this.store.locationInferred = !!location;
      // Resolve the job's coordinates so the search fences the geo filter on a point
      // rather than a server-geocoded string (ONE-6241). Best-effort: a missing or
      // failed lookup degrades to the location string downstream.
      const coordinates = await this.fetchJobCoordinates(jobId);
      if (token !== activeJobSelectionToken) {
        return 'superseded';
      }
      this.store.activeJobCoordinates = coordinates;
      // A distance filter has nothing to anchor to without a location, so clear it when the
      // job has none — mirroring clearActiveJob and SmartFilters.
      if (!location) {
        this.store.distance = undefined;
      } else if (!this.store.distance) {
        // The job's location always fences the search at the default radius server-side, so
        // surface it: seed the sidebar Distance at the default and flag it inferred (sparkle,
        // like LOCATION) instead of leaving it blank while a 30-mi fence silently applies
        // (ONE-6457). A user-set distance is preserved above — it's the recruiter's choice.
        this.store.distance = DEFAULT_AROUND_RADIUS;
        this.store.distanceInferred = true;
      }
      return 'loaded';
    } catch (error) {
      // A deleted/missing job is an expected outcome: hand it back to the caller (which
      // recovers, e.g. the rerun's text-search fallback) without a toast or Sentry report.
      if (error instanceof JobNotFoundError) {
        return 'not-found';
      }
      // Anything else is unexpected — never swallow it: capture and surface a critical toast.
      ErrorService.captureException(error);
      SnackbarService.critical('Could not load that job. Please try again.');
      return 'error';
    }
  }

  /**
   * Re-runs a recent search: restores its input + stored filters and attributes the next
   * session to it (source='recent', parentSessionId). Attribution is one-shot — consumed by
   * the next request in syncFiltersToSourcingSearch, so only the rerun (not subsequent
   * pagination) is linked. Job rows re-select the job by id (titles aren't unique) and fall
   * back to a text search if the job no longer exists; typed rows repopulate the keyword and
   * the stored sidebar filters. The caller executes the search.
   */
  public async applyRecentSearch(recent: RecentSearch): Promise<void> {
    this.source = 'recent';
    this.parentSessionId = recent.id;

    // Clear any prior active job up front so a failed job load (e.g. a deleted job) can't
    // leave a stale job active and block the fallback below.
    this.clearActiveJob();

    if (recent.searchMode === 'job' && recent.jobId != null) {
      const outcome = await this.selectActiveJob(recent.jobId);
      // Only a genuinely deleted job ('not-found') falls back to a text search on the stored
      // title so the rerun still does something, and tells the user why (TDD §5.4). On an
      // unexpected 'error' selectActiveJob already surfaced a critical toast, so we neither
      // stack a second snackbar nor mislead the user that the job is "no longer active".
      if (outcome === 'not-found') {
        this.keyword = recent.documentSearchJobTitle ?? recent.locationText ?? '';
        this.searchMode = detectTypedMode(this.keyword);
        SnackbarService.caution('That job is no longer active — searched as text.');
      }
      return;
    }

    this.keyword = recent.rawInput ?? recent.query ?? '';
    // Restore the stored mode so the rerun routes through the smart wrapper (session
    // recording + rerun attribution); without it the request takes the legacy path.
    this.searchMode = recent.searchMode;

    // Values the original session inferred are restored as inferred (sparkle, recomputed by
    // the next search); the rest were the recruiter's own and are restored as user-set.
    const inferredIds = recent.inferredFilterIds ?? [];
    this.location = recent.locationText ?? undefined;
    this.locationInferred = !!this.location && inferredIds.includes(SelectedFilterType.LOCATION);
    this.distance = recent.aroundRadius ?? undefined;
    this.distanceInferred =
      !!this.distance && inferredIds.includes(SelectedFilterType.AROUND_RADIUS);
    this.lastActive = this.readLastActive(recent);
    this.lastActiveInferred =
      !!this.lastActive && inferredIds.includes(SelectedFilterType.LAST_ACTIVE);

    // Insight filters restore only the ones the session inferred, with their recorded mode,
    // and never over a value the recruiter set now; inferred values from the previous query
    // drop.
    const recordedInferred = recent.appliedFilters.filter((filter) =>
      inferredIds.includes(filter.type),
    );
    this.mergeInferredDossierFilters(
      new Map(recordedInferred.map((filter) => [filter.type, filter])),
    );
  }

  private readLastActive(recent: RecentSearch): number | undefined {
    const filter = recent.appliedFilters.find((f) => f.type === SelectedFilterType.LAST_ACTIVE);
    return filter ? Number(filter.value) : undefined;
  }

  public clearActiveJob(): void {
    this.store.activeJob = undefined;
    this.store.activeJobCoordinates = undefined;
    // The store's searchMode was set to 'job' when the job was selected, so reset it with
    // the job: a stale 'job' would ride into the next jobless request, which the backend
    // rejects for lacking a jobId — silently blanking the results on the next background
    // refresh (ONE-6903). Re-derive the typed mode when a keyword remains (mirroring the
    // recent-search fallback); selecting a job clears the keyword, so this is normally
    // a reset to the default.
    this.store.searchMode = this.keyword ? detectTypedMode(this.keyword) : undefined;
    // Clear the location too when it was inferred from the job — along with the radius seeded
    // beside it (value AND sparkle), so a job-seeded distanceInferred doesn't linger as a
    // stale "From search" flag once the job is gone (ONE-6457).
    if (this.store.locationInferred) {
      this.store.location = undefined;
      this.store.distance = undefined;
      this.store.distanceInferred = false;
      this.store.locationInferred = false;
    }
  }

  public get keyword(): string | undefined {
    return this.store.keyword;
  }

  public set keyword(keyword: string | undefined) {
    this.store.keyword = keyword;
  }

  public get source(): 'typed' | 'recent' {
    return this.store.source;
  }

  public set source(source: 'typed' | 'recent') {
    this.store.source = source;
  }

  public get parentSessionId(): string | null {
    return this.store.parentSessionId;
  }

  public set parentSessionId(parentSessionId: string | null) {
    this.store.parentSessionId = parentSessionId;
  }

  /**
   * UI search-mode intent for a typed search ('natural' | 'keyword_boolean'). Drives the
   * backend wrapper (NL extraction vs Boolean parsing) and the staged-loader gating. Left
   * undefined on the legacy keyword path; job mode is derived from the active job instead.
   */
  public get searchMode(): SearchMode | undefined {
    return this.store.searchMode;
  }

  public set searchMode(searchMode: SearchMode | undefined) {
    this.store.searchMode = searchMode;
  }

  public get jobTitle(): string | undefined {
    return this.store.jobTitle;
  }

  public set jobTitle(jobTitle: string | undefined) {
    this.store.jobTitle = jobTitle;
  }

  public get lastActive(): number | undefined {
    return this.store.lastActive;
  }

  public set lastActive(lastActive: number | undefined) {
    this.store.lastActive = lastActive;
  }

  public get location(): string | undefined {
    return this.store.location;
  }

  public set location(location: string | undefined) {
    this.store.location = location;
  }

  public get distance(): number | undefined {
    return this.store.distance;
  }

  public set distance(distance: number | undefined) {
    this.store.distance = distance;
  }

  public get payRateMin(): number | undefined {
    return this.store.payRateMin;
  }

  public set payRateMin(payRateMin: number | undefined) {
    const changed = payRateMin !== this.store.payRateMin;
    const wasEmpty = !this.hasDossierValue(SelectedFilterType.PAY_RATE_MIN);
    this.store.payRateMin = payRateMin;
    this.onDossierFilterEdited(SelectedFilterType.PAY_RATE_MIN, changed, wasEmpty);
  }

  public get payRateMax(): number | undefined {
    return this.store.payRateMax;
  }

  public set payRateMax(payRateMax: number | undefined) {
    const changed = payRateMax !== this.store.payRateMax;
    const wasEmpty = !this.hasDossierValue(SelectedFilterType.PAY_RATE_MAX);
    this.store.payRateMax = payRateMax;
    this.onDossierFilterEdited(SelectedFilterType.PAY_RATE_MAX, changed, wasEmpty);
  }

  public get shifts(): FilterableShift[] {
    return this.store.shifts ?? [];
  }

  public set shifts(shifts: FilterableShift[]) {
    const changed = shifts.join() !== this.shifts.join();
    const wasEmpty = !this.hasDossierValue(SelectedFilterType.SHIFT);
    this.store.shifts = shifts;
    this.onDossierFilterEdited(SelectedFilterType.SHIFT, changed, wasEmpty);
  }

  public get willingToRelocate(): boolean {
    return this.store.willingToRelocate;
  }

  public set willingToRelocate(willingToRelocate: boolean) {
    const changed = willingToRelocate !== this.store.willingToRelocate;
    const wasEmpty = !this.hasDossierValue(SelectedFilterType.WILLING_TO_RELOCATE);
    this.store.willingToRelocate = willingToRelocate;
    this.onDossierFilterEdited(SelectedFilterType.WILLING_TO_RELOCATE, changed, wasEmpty);
  }

  public get openToTravel(): boolean {
    return this.store.openToTravel;
  }

  public set openToTravel(openToTravel: boolean) {
    const changed = openToTravel !== this.store.openToTravel;
    const wasEmpty = !this.hasDossierValue(SelectedFilterType.OPEN_TO_TRAVEL);
    this.store.openToTravel = openToTravel;
    this.onDossierFilterEdited(SelectedFilterType.OPEN_TO_TRAVEL, changed, wasEmpty);
  }

  public get languages(): FilterableLanguage[] {
    return this.store.languages ?? [];
  }

  public set languages(languages: FilterableLanguage[]) {
    const changed = languages.join() !== this.languages.join();
    const wasEmpty = !this.hasDossierValue(SelectedFilterType.LANGUAGE);
    this.store.languages = languages;
    this.onDossierFilterEdited(SelectedFilterType.LANGUAGE, changed, wasEmpty);
  }

  public get responsiveness(): FilterableResponsiveness[] {
    return this.store.responsiveness ?? [];
  }

  public set responsiveness(responsiveness: FilterableResponsiveness[]) {
    const changed = responsiveness.join() !== this.responsiveness.join();
    const wasEmpty = !this.hasDossierValue(SelectedFilterType.RESPONSIVENESS);
    this.store.responsiveness = responsiveness;
    this.onDossierFilterEdited(SelectedFilterType.RESPONSIVENESS, changed, wasEmpty);
  }

  public get recentlyOpenToWork(): boolean {
    return this.store.recentlyOpenToWork;
  }

  public set recentlyOpenToWork(recentlyOpenToWork: boolean) {
    const changed = recentlyOpenToWork !== this.store.recentlyOpenToWork;
    const wasEmpty = !this.hasDossierValue(SelectedFilterType.LAST_OPEN_TO_OPPORTUNITIES);
    this.store.recentlyOpenToWork = recentlyOpenToWork;
    this.onDossierFilterEdited(SelectedFilterType.LAST_OPEN_TO_OPPORTUNITIES, changed, wasEmpty);
  }

  /**
   * Count shown on the Filters header and the panel toggle. Distance is a radius
   * modifier of Location, so it is not counted on its own, and Pay is one filter
   * however many bounds are set.
   */
  public get activeSidebarFilterCount(): number {
    return [
      this.location,
      this.lastActive,
      this.payRateMin || this.payRateMax,
      this.shifts.length,
      this.willingToRelocate,
      this.openToTravel,
      this.languages.length,
      this.responsiveness.length,
      this.recentlyOpenToWork,
    ].filter(Boolean).length;
  }

  /** True when the backend inferred this insight filter's active value (the sparkle). */
  public isDossierFilterInferred(type: SelectedFilterType): boolean {
    return (
      isDossierFilterType(type) &&
      !!this.dossierFilterState[type]?.inferred &&
      this.hasDossierValue(type)
    );
  }

  private get dossierFilterState(): Partial<Record<SelectedFilterType, DossierFilterState>> {
    return this.store.dossierFilterState ?? {};
  }

  private setDossierFilterState(type: SelectedFilterType, state?: DossierFilterState): void {
    const next = { ...this.dossierFilterState, [type]: state };
    if (!state) {
      delete next[type];
    }
    this.store.dossierFilterState = next;
  }

  private hasDossierValue(type: DossierFilterType): boolean {
    switch (type) {
      case SelectedFilterType.PAY_RATE_MIN:
        return !!this.payRateMin;
      case SelectedFilterType.PAY_RATE_MAX:
        return !!this.payRateMax;
      case SelectedFilterType.SHIFT:
        return this.shifts.length > 0;
      case SelectedFilterType.WILLING_TO_RELOCATE:
        return this.willingToRelocate;
      case SelectedFilterType.OPEN_TO_TRAVEL:
        return this.openToTravel;
      case SelectedFilterType.LANGUAGE:
        return this.languages.length > 0;
      case SelectedFilterType.RESPONSIVENESS:
        return this.responsiveness.length > 0;
      case SelectedFilterType.LAST_OPEN_TO_OPPORTUNITIES:
        return this.recentlyOpenToWork;
      default:
        return unhandledDossierType(type);
    }
  }

  /** Writes a wire-shaped value (pay as a numeric string, lists as arrays) into its control. */
  private writeDossierValue(type: DossierFilterType, value: unknown): void {
    const list = Array.isArray(value) ? value.join(',') : undefined;
    switch (type) {
      case SelectedFilterType.PAY_RATE_MIN:
        this.store.payRateMin = readPayRate(value);
        return;
      case SelectedFilterType.PAY_RATE_MAX:
        this.store.payRateMax = readPayRate(value);
        return;
      case SelectedFilterType.SHIFT:
        this.store.shifts = readListParam(list, FILTERABLE_SHIFTS);
        return;
      case SelectedFilterType.WILLING_TO_RELOCATE:
        this.store.willingToRelocate = value === true || value === 'true';
        return;
      case SelectedFilterType.OPEN_TO_TRAVEL:
        this.store.openToTravel = value === true || value === 'true';
        return;
      case SelectedFilterType.LANGUAGE:
        this.store.languages = readListParam(list, FILTERABLE_LANGUAGES);
        return;
      case SelectedFilterType.RESPONSIVENESS:
        this.store.responsiveness = readListParam(list, FILTERABLE_RESPONSIVENESS);
        return;
      // The sidebar only offers the fixed RECENT_OPEN_TO_WORK_DAYS window, so any day count
      // reads as "on".
      case SelectedFilterType.LAST_OPEN_TO_OPPORTUNITIES:
        this.store.recentlyOpenToWork = value != null && value !== '';
        return;
      default:
        unhandledDossierType(type);
    }
  }

  /**
   * A recruiter edit of an insight filter. A fresh set (empty to nonempty) starts strict.
   * Emptying it resets ownership and mode, and suppresses re-inference if the value came from
   * inference (even after an edit took it over). Any other change makes it user-owned but keeps
   * its mode.
   */
  private onDossierFilterEdited(type: DossierFilterType, changed: boolean, wasEmpty = false): void {
    const state = this.dossierFilterState[type];
    if (!state) {
      // Strict, like inferred filters: only candidates with the value on file. The checkbox opts
      // in to unknowns. The pay floor has no mode control, so it never records one.
      if (wasEmpty && this.hasDossierValue(type) && type !== SelectedFilterType.PAY_RATE_MIN) {
        this.setDossierFilterState(type, { inferred: false, includeUnknown: false });
      }
      return;
    }

    // Not gated on `changed`: after unstagePayRange the value is already empty.
    if (!this.hasDossierValue(type)) {
      if (state.inferred || state.takenOver) {
        this.suppressClearedType(type);
      }
      this.setDossierFilterState(type, undefined);
    } else if (changed && state.inferred) {
      this.takeOverDossierFilter(type);
    }
  }

  public isDossierFilterActive(type: SelectedFilterType): boolean {
    return isDossierFilterType(type) && this.hasDossierValue(type);
  }

  /** The filter's mode: true keeps candidates with no answer on file. */
  public getDossierIncludeUnknown(type: SelectedFilterType): boolean {
    return this.isDossierFilterActive(type) && !!this.dossierFilterState[type]?.includeUnknown;
  }

  /**
   * Flipping the mode is an edit: an inferred filter becomes the recruiter's and is sent with
   * the new mode. The value stays, so nothing is suppressed.
   */
  public setDossierIncludeUnknown(type: SelectedFilterType, includeUnknown: boolean): void {
    if (!this.isDossierFilterActive(type)) {
      return;
    }
    const state = this.dossierFilterState[type];
    const takenOver = !!state?.inferred || !!state?.takenOver;
    this.setDossierFilterState(type, {
      inferred: false,
      includeUnknown,
      ...(takenOver ? { takenOver } : {}),
    });
  }

  private takeOverDossierFilter(type: DossierFilterType): void {
    this.setDossierFilterState(type, {
      inferred: false,
      includeUnknown: !!this.dossierFilterState[type]?.includeUnknown,
      takenOver: true,
    });
  }

  /**
   * The backend suppresses both pay bounds when either is suppressed, so an inferred opposite
   * bound is taken over (value and mode kept) rather than dropped by the next response.
   */
  private suppressClearedType(type: DossierFilterType): void {
    this.suppressInferredType(type);
    const opposite = OPPOSITE_PAY_BOUND[type];
    if (opposite && this.isDossierFilterInferred(opposite)) {
      this.takeOverDossierFilter(opposite);
    }
  }

  /**
   * Merges inferred insight filters by the same rules as location/last-active: user-owned
   * values win, an inferred type takes the response's value and mode, and an AI-owned value
   * the response dropped is cleared.
   */
  private mergeInferredDossierFilters(
    byType: ReadonlyMap<SelectedFilterType, SelectedFilter>,
  ): void {
    for (const type of DOSSIER_FILTER_TYPES) {
      const state = this.dossierFilterState[type];
      if (this.hasDossierValue(type) && !state?.inferred) {
        continue;
      }

      const filter = byType.get(type);
      if (filter) {
        this.writeDossierValue(type, filter.value);
      } else if (state?.inferred) {
        this.writeDossierValue(type, undefined);
      }

      if (!this.hasDossierValue(type)) {
        this.setDossierFilterState(type, undefined);
      } else if (filter) {
        this.setDossierFilterState(type, {
          inferred: true,
          includeUnknown: filter.includeUnknown === true,
        });
      }
    }
  }

  /**
   * Unstages both pay bounds while the inputs hold a contradictory range. The recruiter is
   * mid-correction, not clearing: the bound they typed in is taken over and the other keeps its
   * owner, and both keep their mode for when the range resolves.
   */
  public unstagePayRange(
    edited: SelectedFilterType.PAY_RATE_MIN | SelectedFilterType.PAY_RATE_MAX,
  ): void {
    // The stored flag, not isDossierFilterInferred: an earlier unstage already cleared both values.
    if (this.dossierFilterState[edited]?.inferred) {
      this.takeOverDossierFilter(edited);
    }
    // A strict bound has no entry; record it, or restoring it would read as a fresh manual set.
    for (const type of [
      SelectedFilterType.PAY_RATE_MIN,
      SelectedFilterType.PAY_RATE_MAX,
    ] as const) {
      if (this.hasDossierValue(type) && !this.dossierFilterState[type]) {
        this.setDossierFilterState(type, { inferred: false, includeUnknown: false });
      }
    }
    this.store.payRateMin = undefined;
    this.store.payRateMax = undefined;
  }

  /**
   * Stages both pay inputs; only the bound the recruiter typed in counts as an edit, so a
   * bound restored after unstagePayRange keeps its owner.
   */
  public stagePayRange(
    payRateMin: number | undefined,
    payRateMax: number | undefined,
    edited: SelectedFilterType.PAY_RATE_MIN | SelectedFilterType.PAY_RATE_MAX,
  ): void {
    const minChanged =
      edited === SelectedFilterType.PAY_RATE_MIN && payRateMin !== this.store.payRateMin;
    const maxChanged =
      edited === SelectedFilterType.PAY_RATE_MAX && payRateMax !== this.store.payRateMax;
    // Emptiness of both bounds, whichever was typed in: after unstagePayRange the untouched bound
    // is empty too, and restoring it with no recorded mode is a fresh set.
    const minWasEmpty = !this.store.payRateMin;
    const maxWasEmpty = !this.store.payRateMax;
    this.store.payRateMin = payRateMin;
    this.store.payRateMax = payRateMax;
    this.onDossierFilterEdited(SelectedFilterType.PAY_RATE_MIN, minChanged, minWasEmpty);
    this.onDossierFilterEdited(SelectedFilterType.PAY_RATE_MAX, maxChanged, maxWasEmpty);
  }

  /**
   * Clear all: suppresses each insight filter that came from inference, then resets values,
   * owners and modes.
   */
  public clearHardFilters(): void {
    for (const type of DOSSIER_FILTER_TYPES) {
      const state = this.dossierFilterState[type];
      if (state?.inferred || state?.takenOver) {
        this.suppressInferredType(type);
      }
    }
    this.store.dossierFilterState = {};
    this.store.payRateMin = undefined;
    this.store.payRateMax = undefined;
    this.store.shifts = [];
    this.store.willingToRelocate = false;
    this.store.openToTravel = false;
    this.store.languages = [];
    this.store.responsiveness = [];
    this.store.recentlyOpenToWork = false;
  }

  private get hasStagedHardFilters(): boolean {
    return !!(
      this.payRateMin ||
      this.payRateMax ||
      this.shifts.length ||
      this.willingToRelocate ||
      this.openToTravel ||
      this.languages.length ||
      this.responsiveness.length ||
      this.recentlyOpenToWork
    );
  }

  public set page(page: number | undefined) {
    this.store.page = page;
  }

  public get page(): number | undefined {
    return this.store.page;
  }

  public get itemsPerPage(): number | undefined {
    return this.store.itemsPerPage;
  }

  public set itemsPerPage(itemsPerPage: number | undefined) {
    this.store.itemsPerPage = itemsPerPage;
  }

  public get isSearchingCandidates(): boolean {
    return this.store.isSearchingCandidates;
  }

  public set isSearchingCandidates(isSearchingCandidates: boolean) {
    this.store.isSearchingCandidates = isSearchingCandidates;
  }

  public get candidateListScrollTop(): number {
    return this.store.candidateListScrollTop;
  }

  public set candidateListScrollTop(candidateListScrollTop: number) {
    this.store.candidateListScrollTop = candidateListScrollTop;
  }

  public get alreadyExecutedASearch(): boolean {
    return this.store.alreadyExecutedASearch;
  }

  public set alreadyExecutedASearch(alreadyExecutedASearch: boolean) {
    this.store.alreadyExecutedASearch = alreadyExecutedASearch;
  }

  /**
   * Syncs the current filters to the sourcing search service
   * @param searchService The sourcing search service instance to sync filters to
   */
  public syncFiltersToSourcingSearch(searchService: SourcingSearchService): void {
    // Clear existing filters
    searchService.selectedFilters = [];

    const activeJob = this.store.activeJob;

    if (activeJob) {
      // Job mode: the query is the (stripped) job description, ranked as a document;
      // searchMode/jobId/documentSearchJobTitle drive the backend document-search path.
      searchService.selectedFilters.push({
        type: SelectedFilterType.KEYWORD,
        value: stripHtml(activeJob.description ?? ''),
      });
      searchService.searchMode = 'job';
      searchService.jobId = activeJob.id;
      searchService.queryMode = 'document';
      searchService.documentSearchJobTitle = getJobTitle(activeJob, false).displayTitle;
      // The job's coordinates ARE the search location until the user changes or clears it
      // (onLocationChange / clearAll clear activeJobCoordinates). Send them whenever present —
      // independent of the sparkle flag — so a job with coordinates but no city/state label
      // still fences correctly. The display label rides on the LOCATION chip (locationText).
      searchService.jobLocationCoordinates = this.store.activeJobCoordinates;
    } else {
      // Typed search: forward the UI mode the recruiter's chip (or auto-detection)
      // resolved so the backend wrapper runs NL extraction or Boolean parsing. The legacy
      // search form never set a mode, so its requests stayed on today's keyword path.
      // Never forward 'job' here: without an active job there is no jobId to send with it,
      // and the backend rejects a jobless job-mode request (ONE-6903) — so a 'job' that
      // survived past its job is dropped rather than composed into an invalid request.
      searchService.searchMode =
        this.store.searchMode === 'job' ? undefined : this.store.searchMode;
      searchService.jobId = undefined;
      searchService.queryMode = undefined;
      searchService.documentSearchJobTitle = undefined;
      searchService.jobLocationCoordinates = undefined;

      if (this.keyword) {
        const filter: SelectedFilter = {
          type: SelectedFilterType.KEYWORD,
          value: this.keyword,
        };
        searchService.selectedFilters.push(filter);
      }
    }

    // Job title was a legacy hard filter (exact normalizedJobTitle match). Smart
    // input has no job-title field, so any value — left in localStorage, the ?jobTitle= URL
    // query, or carried over by a recent-search rerun — is stale and would silently collapse
    // recall to candidates whose normalized title is exactly that string. Scrub it here, at the
    // single choke point every search path funnels through, so the stale value can't ride in as
    // a hard filter, leak back into the URL, or linger in localStorage. A stated title still
    // flows via the semantic/keyword path.
    this.jobTitle = undefined;

    // Inferred location, distance and last-active are display state, not request state: the
    // backend recomputes them from the query on every natural search, so sending them back
    // would make a stale inference read as user-set and leak into unrelated searches
    // (ONE-6178). Only user-set values travel — except in job mode, where the job-seeded
    // LOCATION is the search's geo label and must be sent with the request.
    const sendInferred = !!activeJob;

    if (this.lastActive && (sendInferred || !this.lastActiveInferred)) {
      const filter: SelectedFilter = {
        type: SelectedFilterType.LAST_ACTIVE,
        value: this.lastActive,
      };
      searchService.selectedFilters.push(filter);
    }

    if (this.location && (sendInferred || !this.locationInferred)) {
      const filter: SelectedFilter = {
        type: SelectedFilterType.LOCATION,
        value: this.location,
      };
      searchService.selectedFilters.push(filter);
    }

    if (this.distance && (sendInferred || !this.distanceInferred)) {
      const filter: SelectedFilter = {
        type: SelectedFilterType.AROUND_RADIUS,
        value: this.distance,
      };
      searchService.selectedFilters.push(filter);
    }

    // Insight filters follow the same rule: inferred ones stay display state. A user-owned one
    // goes out with its mode; a value with no recorded mode predates modes and stays strict.
    for (const type of DOSSIER_FILTER_TYPES) {
      const state = this.dossierFilterState[type];
      if (!this.hasDossierValue(type) || (!sendInferred && state?.inferred)) {
        continue;
      }
      searchService.selectedFilters.push({
        ...this.dossierRequestFilter(type),
        includeUnknown: state?.includeUnknown ?? false,
      });
    }

    // Suppressions (inferred filters the recruiter X'd) apply to re-runs of the same query;
    // an edited query re-infers from scratch.
    if (this.store.suppressedForKeyword !== this.keyword) {
      this.store.suppressedInferredTypes = [];
      this.store.suppressedForKeyword = undefined;
    }
    const suppressedInferredTypes = this.store.suppressedInferredTypes ?? [];
    searchService.suppressInferredTypes = suppressedInferredTypes.length
      ? [...suppressedInferredTypes]
      : undefined;

    if (this.page) {
      searchService.pagination.page = this.page;
    }

    if (this.itemsPerPage) {
      searchService.pagination.itemsPerPage = this.itemsPerPage;
    }

    // Forward rerun attribution to the request, then consume it so only the first search
    // after a rerun is linked; pagination and later searches are fresh 'typed' sessions.
    searchService.source = this.source;
    searchService.parentSessionId = this.parentSessionId;
    this.store.source = 'typed';
    this.store.parentSessionId = null;
  }

  private dossierRequestFilter(type: DossierFilterType): SelectedFilter {
    switch (type) {
      case SelectedFilterType.PAY_RATE_MIN:
        return { type, value: this.payRateMin ?? 0 };
      case SelectedFilterType.PAY_RATE_MAX:
        return { type, value: this.payRateMax ?? 0 };
      case SelectedFilterType.SHIFT:
        return { type, value: [...this.shifts] };
      case SelectedFilterType.WILLING_TO_RELOCATE:
      case SelectedFilterType.OPEN_TO_TRAVEL:
        return { type, value: true };
      case SelectedFilterType.LANGUAGE:
        return { type, value: [...this.languages] };
      case SelectedFilterType.RESPONSIVENESS:
        return { type, value: [...this.responsiveness] };
      case SelectedFilterType.LAST_OPEN_TO_OPPORTUNITIES:
        return { type, value: RECENT_OPEN_TO_WORK_DAYS };
      default:
        return unhandledDossierType(type);
    }
  }

  public setSelectedFiltersFromUrlQuery(urlQuery: Record<string, string | null>): void {
    this.keyword = urlQuery.keyword || undefined;
    this.location = urlQuery.location || undefined;
    this.distance = urlQuery.distance ? Number(urlQuery.distance) : undefined;
    this.jobTitle = urlQuery.jobTitle || undefined;
    this.lastActive = urlQuery.lastActive ? Number(urlQuery.lastActive) : undefined;
    this.page = urlQuery.page ? Number(urlQuery.page) : undefined;
    this.itemsPerPage = urlQuery.itemsPerPage ? Number(urlQuery.itemsPerPage) : undefined;

    // Hard filters round-trip like the other sidebar values (ONE-6510). Values are
    // validated on read — a hand-edited param falls back to "unset" rather than staging
    // something the server would silently no-op.
    const payRateMin = readPayRate(urlQuery.payRateMin);
    const payRateMax = readPayRate(urlQuery.payRateMax);
    // A contradictory range matches nothing the recruiter intends; the pay controls
    // refuse to stage one, so a hydrated one is refused the same way.
    const contradictoryPayRange =
      payRateMin !== undefined && payRateMax !== undefined && payRateMin > payRateMax;
    // Ownership and mode are rebuilt from the URL below, so these write the store directly: an
    // edit would give a value with no recorded mode the manual default, and old links are strict.
    // Takeover marks aren't in the URL; keep this session's so the round-trip after every
    // search doesn't drop them.
    const takenOverTypes = DOSSIER_FILTER_TYPES.filter(
      (type) => this.dossierFilterState[type]?.takenOver,
    );
    this.store.dossierFilterState = {};
    this.store.payRateMin = contradictoryPayRange ? undefined : payRateMin;
    this.store.payRateMax = contradictoryPayRange ? undefined : payRateMax;
    this.store.shifts = readListParam(urlQuery.shifts, FILTERABLE_SHIFTS);
    this.store.willingToRelocate = urlQuery.willingToRelocate === 'true';
    this.store.openToTravel = urlQuery.openToTravel === 'true';
    this.store.languages = readListParam(urlQuery.languages, FILTERABLE_LANGUAGES);
    this.store.responsiveness = readListParam(urlQuery.responsiveness, FILTERABLE_RESPONSIVENESS);
    this.store.recentlyOpenToWork = urlQuery.recentlyOpenToWork === 'true';

    // Restore which managed filters were AI-inferred (the sparkle) so a reloaded or
    // shared deep-link keeps them inferred instead of promoting them to user-set hard
    // filters — a promoted value gets sent by syncFiltersToSourcingSearch and suppresses
    // the next query's re-inference, leaving "clear everything" as the only way back. A
    // type counts as inferred only when its value is present (mirrors applyRecentSearch's
    // inferredFilterIds restore). No `inferred` param means everything is user-set.
    // A duplicated query key (?inferred=A&inferred=B) parses as a string[] at runtime
    // despite the declared type, so split only a string and treat anything else as absent.
    const inferredParam = urlQuery.inferred;
    const inferredTypes = typeof inferredParam === 'string' ? inferredParam.split(',') : [];
    this.locationInferred = !!this.location && inferredTypes.includes(SelectedFilterType.LOCATION);
    this.distanceInferred =
      !!this.distance && inferredTypes.includes(SelectedFilterType.AROUND_RADIUS);
    this.lastActiveInferred =
      !!this.lastActive && inferredTypes.includes(SelectedFilterType.LAST_ACTIVE);

    // Insight filters also carry their mode (`includeUnknown` lists the lenient ones); both
    // annotations count only beside an active value.
    const includeUnknownParam = urlQuery.includeUnknown;
    const includeUnknownTypes =
      typeof includeUnknownParam === 'string' ? includeUnknownParam.split(',') : [];
    for (const type of DOSSIER_FILTER_TYPES) {
      const inferred = inferredTypes.includes(type);
      const includeUnknown = includeUnknownTypes.includes(type);
      const takenOver = !inferred && takenOverTypes.includes(type);
      if (this.hasDossierValue(type) && (inferred || includeUnknown || takenOver)) {
        this.setDossierFilterState(type, {
          inferred,
          includeUnknown,
          ...(takenOver ? { takenOver } : {}),
        });
      }
    }
  }

  public getUrlQueryFromSelectedFilters(): Record<string, string | null> {
    const urlQuery: Record<string, string | null> = {};

    if (this.keyword) {
      urlQuery.keyword = this.keyword;
    }

    if (this.location) {
      urlQuery.location = this.location;
    }

    if (this.distance) {
      urlQuery.distance = `${this.distance}`;
    }

    // Job title is never serialized: smart input has no job-title field and any stale value is
    // scrubbed from state at search time. Read stays permissive in
    // setSelectedFiltersFromUrlQuery so shared legacy links still hydrate.

    if (this.lastActive) {
      urlQuery.lastActive = `${this.lastActive}`;
    }

    if (this.page) {
      urlQuery.page = `${this.page}`;
    }

    if (this.itemsPerPage) {
      urlQuery.itemsPerPage = `${this.itemsPerPage}`;
    }

    if (this.payRateMin) {
      urlQuery.payRateMin = `${this.payRateMin}`;
    }

    if (this.payRateMax) {
      urlQuery.payRateMax = `${this.payRateMax}`;
    }

    if (this.shifts.length) {
      urlQuery.shifts = this.shifts.join(',');
    }

    if (this.willingToRelocate) {
      urlQuery.willingToRelocate = 'true';
    }

    if (this.openToTravel) {
      urlQuery.openToTravel = 'true';
    }

    if (this.languages.length) {
      urlQuery.languages = this.languages.join(',');
    }

    if (this.responsiveness.length) {
      urlQuery.responsiveness = this.responsiveness.join(',');
    }

    // A day count in the URL would be a value the sidebar can neither render nor clear.
    if (this.recentlyOpenToWork) {
      urlQuery.recentlyOpenToWork = 'true';
    }

    // Serialize which managed filters are AI-inferred so a reload or shared deep-link
    // restores their sparkle and keeps them out of the request (the backend re-infers each
    // query); without this they read back as user-set values that fence the search and
    // block re-inference. Emitted only alongside the value it annotates.
    const inferredTypes: string[] = [];
    if (this.location && this.locationInferred) {
      inferredTypes.push(SelectedFilterType.LOCATION);
    }
    if (this.distance && this.distanceInferred) {
      inferredTypes.push(SelectedFilterType.AROUND_RADIUS);
    }
    if (this.lastActive && this.lastActiveInferred) {
      inferredTypes.push(SelectedFilterType.LAST_ACTIVE);
    }
    const includeUnknownTypes: string[] = [];
    for (const type of DOSSIER_FILTER_TYPES) {
      const state = this.dossierFilterState[type];
      if (!state || !this.hasDossierValue(type)) {
        continue;
      }
      if (state.inferred) {
        inferredTypes.push(type);
      }
      if (state.includeUnknown) {
        includeUnknownTypes.push(type);
      }
    }
    if (inferredTypes.length) {
      urlQuery.inferred = inferredTypes.join(',');
    }
    if (includeUnknownTypes.length) {
      urlQuery.includeUnknown = includeUnknownTypes.join(',');
    }

    return urlQuery;
  }

  /**
   * Checks if there are any filters set
   */
  public hasFilters(): boolean {
    return !!(
      this.keyword ||
      this.jobTitle ||
      this.lastActive ||
      this.location ||
      this.hasStagedHardFilters
    );
  }

  public urlQueryHasFilters(urlQuery: Record<string, string | null>): boolean {
    return !!(
      urlQuery.keyword ||
      urlQuery.location ||
      urlQuery.distance ||
      urlQuery.jobTitle ||
      urlQuery.lastActive ||
      urlQuery.payRateMin ||
      urlQuery.payRateMax ||
      urlQuery.shifts ||
      urlQuery.willingToRelocate ||
      urlQuery.openToTravel ||
      urlQuery.languages ||
      urlQuery.responsiveness ||
      urlQuery.recentlyOpenToWork
    );
  }

  public async resolveUrlQuery(
    urlQuery: LocationQuery | LocationQuery[],
    options?: { localStateIsPriority: boolean },
  ): Promise<Record<string, string | null> | undefined> {
    try {
      if (Array.isArray(urlQuery)) {
        return;
      }

      const validUrlQuery = urlQuery as unknown as Record<string, string | null>;
      const hasUrlQueryFilters = this.urlQueryHasFilters(validUrlQuery);
      let newUrlQuery = this.getUrlQueryFromSelectedFilters();

      if (newUrlQuery && options?.localStateIsPriority) {
        this.setSelectedFiltersFromUrlQuery(newUrlQuery);
      } else if (hasUrlQueryFilters) {
        this.setSelectedFiltersFromUrlQuery(validUrlQuery);
        newUrlQuery = validUrlQuery;
      }

      return newUrlQuery;
    } catch (error) {
      ErrorService.captureException(error);
    }
  }

  /**
   * Merges natural-language inferred filters from a search response into the sidebar.
   *
   * Each managed field (LOCATION / AROUND_RADIUS / LAST_ACTIVE) is one of: user-owned (a
   * value the user set — sparkle off), inferred (a value the AI extracted — sparkle on),
   * or empty. User-owned values always win: they're never overwritten or sparkled (the
   * backend already excludes user-set types, so this is the FE-side guarantee). A field
   * the response no longer infers drops its sparkle and its AI-owned value, so a fresh
   * query re-infers from scratch while user edits stick.
   *
   * The insight filters follow the same rules per backend type, carrying each inferred
   * filter's mode.
   *
   * No-op in job mode: the active job tracks its LOCATION sparkle FE-locally and the
   * backend never echoes job-derived filters back here.
   */
  public applyInferredFilters(inferredFilters: SelectedFilter[] = []): void {
    if (this.store.activeJob) {
      return;
    }

    const byType = new Map(inferredFilters.map((filter) => [filter.type, filter]));

    // LOCATION (string)
    const location = byType.get(SelectedFilterType.LOCATION);
    const locationUserOwned = !!this.store.location && !this.store.locationInferred;
    if (location) {
      if (!locationUserOwned) {
        this.store.location = location.value as string;
        this.store.locationInferred = true;
      }
    } else if (this.store.locationInferred) {
      this.store.location = undefined;
      this.store.locationInferred = false;
    }

    // AROUND_RADIUS → Distance (miles). Distance is a radius modifier of Location, never a
    // standalone filter (the sidebar disables it without a location), so only apply an
    // inferred distance when a location survives this merge — either inferred now or
    // user-owned. Otherwise drop any stale inferred distance so it isn't sent unanchored.
    const distance = byType.get(SelectedFilterType.AROUND_RADIUS);
    const distanceUserOwned = !!this.store.distance && !this.store.distanceInferred;
    const hasLocation = !!location || locationUserOwned;
    if (distance && hasLocation) {
      if (!distanceUserOwned) {
        this.store.distance = distance.value as number;
        this.store.distanceInferred = true;
      }
    } else if (this.store.distanceInferred) {
      this.store.distance = undefined;
      this.store.distanceInferred = false;
    }

    // LAST_ACTIVE (days)
    const lastActive = byType.get(SelectedFilterType.LAST_ACTIVE);
    const lastActiveUserOwned = !!this.store.lastActive && !this.store.lastActiveInferred;
    if (lastActive) {
      if (!lastActiveUserOwned) {
        this.store.lastActive = lastActive.value as number;
        this.store.lastActiveInferred = true;
      }
    } else if (this.store.lastActiveInferred) {
      this.store.lastActive = undefined;
      this.store.lastActiveInferred = false;
    }

    this.mergeInferredDossierFilters(byType);
  }

  public async searchCandidatesWithAdditionalInformation(options?: {
    page?: number;
    itemsPerPage?: number;
    resetTab?: boolean;
    // Freshness gate forwarded to the sourcing search write AND the inferred-filter merge, so
    // a response superseded mid-flight (a clear or a newer search) is never committed to the
    // store — neither its results nor its inferred sidebar filters can overwrite the newer
    // search's. The unlock/messaging refreshes and every typed search pass this.
    shouldCommitResults?: () => boolean;
  }): Promise<{ fellBackToKeyword: boolean }> {
    const meStore = useMeStore();
    const sourcingSearchService = new SourcingSearchService();
    const jobApplicantService = new JobApplicantService();
    const viewedCandidateService = new ViewedCandidateService();
    const employerId = meStore.employer?.id as unknown as number;

    if (options?.page) {
      this.page = options.page;
    }

    if (options?.itemsPerPage) {
      this.itemsPerPage = options.itemsPerPage;
    }

    this.syncFiltersToSourcingSearch(sourcingSearchService);

    if (options?.resetTab !== false) {
      sourcingSearchService.activeTab = SearchTab.MATCHES;
    }

    try {
      viewedCandidateService.viewedCandidates = [];

      const result = await sourcingSearchService.searchCandidates({
        shouldCommitResults: options?.shouldCommitResults,
        ...(options?.resetTab === false ? { clearSelectedCandidatesOnSessionChange: true } : {}),
      });

      // A superseded response is gated out of the sidebar merge the same way searchCandidates
      // gates it out of the results store — otherwise its location/distance/last-active would
      // overwrite the newer search's inferred filters.
      if (options?.shouldCommitResults && !options.shouldCommitResults()) {
        return { fellBackToKeyword: result.fellBackToKeyword ?? false };
      }

      // Reflect any natural-language inferred filters from the response in the sidebar
      // (sparkle state); a no-op in job mode and when the response carries none.
      this.applyInferredFilters(result.inferredFilters);

      const userProfileIds = result.hits.map((candidate) => Number(candidate.profileId));

      // Supplemental job-applicant fetch for the ProPreviewCard — a failure
      // here must not wipe out the primary search results.
      // (Viewed-candidates are already fetched inside searchCandidates().)
      try {
        await jobApplicantService.getJobApplicantsByUserProfileIds(employerId, userProfileIds);
      } catch (jobApplicantError) {
        ErrorService.captureException(jobApplicantError);
      }

      // Surfaced to the caller (the smart search view) so it can show a fallback toast when
      // a natural-language query degraded to a literal keyword search on the backend.
      return { fellBackToKeyword: result.fellBackToKeyword ?? false };
    } catch (error) {
      sourcingSearchService.candidates = []; // Reset the candidates only if search fails
      throw error; // Still need to throw the error the calling function will handle it
    } finally {
      this.alreadyExecutedASearch = true;
    }
  }
}
